import { Component, computed, DestroyRef, inject, Input, OnInit, signal } from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms'
import { NgbActiveModal, NgbAlertModule } from '@ng-bootstrap/ng-bootstrap'
import { NgSelectModule } from '@ng-select/ng-select'
import { ToastrService } from 'ngx-toastr'
import { map, startWith } from 'rxjs'
import { StaffTypeService } from 'src/app/core/services/staff/staff-type.service'
import { StaffService } from 'src/app/core/services/staff/staff.service'
import { ButtonComponent } from 'src/app/shared/components/form-elements/button/button.component'
import { InputTextareaComponent } from 'src/app/shared/components/form-elements/input-textarea/input-textarea.component'
import { InputComponent } from 'src/app/shared/components/form-elements/input/input.component'
import { LabelComponent } from 'src/app/shared/components/form-elements/label/label.component'
import { SwitchComponent } from 'src/app/shared/components/form-elements/switch/switch.component'
import { StaffSelectOptionComponent } from 'src/app/shared/components/staff/staff-select-option/staff-select-option.component'

@Component({
    selector: 'sf-send-staff-push-notification-modal',
    imports: [
        ButtonComponent,
        ReactiveFormsModule,
        NgbAlertModule,
        NgSelectModule,
        LabelComponent,
        SwitchComponent,
        InputComponent,
        InputTextareaComponent,
        StaffSelectOptionComponent,
    ],
    templateUrl: './send-staff-push-notification-modal.component.html',
    styleUrl: './send-staff-push-notification-modal.component.scss',
})
export class SendStaffPushNotificationModalComponent implements OnInit {
    #fb = inject(FormBuilder)
    #staffService = inject(StaffService)
    #staffTypeService = inject(StaffTypeService)
    #toastr = inject(ToastrService)
    #destroyRef = inject(DestroyRef)

    activeModal = inject(NgbActiveModal)

    type = signal<'all' | 'users' | 'role'>('users')

    changeType(type: 'all' | 'users' | 'role') {
        this.type.set(type)

        switch (type) {
            case 'all':
                this.form.controls.allUsers.setValue(true)
                this.form.controls.userIds.setValue([])
                this.form.controls.roleId.setValue(null)
                break
            case 'users':
                this.form.controls.allUsers.setValue(false)
                this.form.controls.userIds.setValue(this.#userIds ? this.#userIds : [])
                this.form.controls.roleId.setValue(null)
                break
            case 'role':
                this.form.controls.allUsers.setValue(false)
                this.form.controls.userIds.setValue([])
                this.form.controls.roleId.setValue(null)
                break
        }
    }

    #userIds: number[] | null = null
    @Input() set userIds(value: number[] | number | null) {
        this.#userIds = Array.isArray(value) ? value : value ? [value] : null
        this.form.controls.userIds.setValue(this.#userIds ? this.#userIds : [])
    }
    get userIds() {
        return this.#userIds
    }

    allStaff = toSignal(this.#staffService.getAllStaff(), { initialValue: [] })
    allRoles = toSignal(this.#staffTypeService.getAllStaffTypes(), { initialValue: [] })

    form = this.#fb.group({
        allUsers: this.#fb.nonNullable.control<boolean>(false),
        userIds: this.#fb.nonNullable.control<number[]>([]),
        roleId: this.#fb.nonNullable.control<number | null>(null),
        title: this.#fb.nonNullable.control<string>('', [Validators.required, Validators.maxLength(50)]),
        body: this.#fb.nonNullable.control<string>('', [Validators.required, Validators.maxLength(200)]),
        highPriority: this.#fb.nonNullable.control<boolean>(false),
    })

    userCount = toSignal(
        this.form.controls.userIds.valueChanges.pipe(
            startWith(this.form.controls.userIds.value),
            map((x) => (Array.isArray(x) ? x.length : 0))
        ),
        {
            initialValue: Array.isArray(this.form.controls.userIds.value) ? this.form.controls.userIds.value.length : 0,
        }
    )

    sendButtonText = computed(() => {
        if (this.type() === 'all') {
            return 'Send to All Users'
        } else if (this.type() === 'role') {
            return 'Send to selected role'
        } else {
            return `Send to ${this.userCount()} users`
        }
    })

    ngOnInit() {
        this.form.markAsPristine()
        this.form.markAsUntouched()

        this.form.controls.userIds.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
            this.#userIds = value
        })
    }

    sendNotification() {
        this.form.markAllAsTouched()

        if (this.form.invalid) {
            this.#toastr.error('Please correct the errors in the form before submitting.')
            return
        }

        const request = {
            allUsers: this.form.controls.allUsers.value,
            userIds: this.form.controls.userIds.value,
            roleId: this.form.controls.roleId.value,
            title: this.form.controls.title.value,
            body: this.form.controls.body.value,
            highPriority: this.form.controls.highPriority.value,
        }

        this.#staffService.sendPushNotification(request).subscribe({
            next: () => {
                this.activeModal.close('Notification sent')
                this.#toastr.success('Push notification sent successfully')
            },
            error: (err) => {
                console.error('Error sending notification:', err)
                this.#toastr.error('Failed to send push notification')
            },
        })
    }
}
